import type { CaseStatus, DocStatus, ShipmentStatus } from "../api/types.ts"
import { useT } from "../i18n/index.tsx"
import { OWNER_ICON, type CaseOwner } from "../lib/case-owner.ts"
import { Badge, type BadgeTone, type IconName } from "../ui/index.ts"

/** 상태 → 색. 한 곳에 모아 두어야 목록·상세·옆판이 같은 색으로 보인다. */
const CASE_TONE: Record<CaseStatus, BadgeTone> = {
  registered: "warn",
  arrived: "accent",
  documented: "accent",
  shipped: "info",
  returned: "ok",
  discarded: "neutral",
  cancelled: "neutral",
}

/** 상태 → 모양. 색과 짝을 이룬다(색약·흑백 인쇄 대비). */
const CASE_ICON: Record<CaseStatus, IconName> = {
  registered: "clock",
  arrived: "inbox",
  documented: "doc",
  shipped: "ship",
  returned: "check",
  discarded: "trash",
  cancelled: "close",
}

const DOC_TONE: Record<DocStatus, BadgeTone> = {
  pending: "neutral",
  filled: "info",
  review_requested: "danger",
  confirmed: "ok",
}

const SHIPMENT_TONE: Record<ShipmentStatus, BadgeTone> = {
  draft: "neutral",
  shipped: "accent",
  osaka_arrived: "accent",
  sailed: "info",
  delivered: "ok",
}

export function StatusBadge({ status }: { status: CaseStatus }) {
  const t = useT()
  return (
    <Badge tone={CASE_TONE[status]} icon={CASE_ICON[status]}>
      {t(`status.${status}`)}
    </Badge>
  )
}

export function DocBadge({ docStatus }: { docStatus: DocStatus }) {
  const t = useT()
  return <Badge tone={DOC_TONE[docStatus]}>{t(`doc.${docStatus}`)}</Badge>
}

export function ShipmentBadge({ status }: { status: ShipmentStatus }) {
  const t = useT()
  return <Badge tone={SHIPMENT_TONE[status]}>{t(`shipment.${status}`)}</Badge>
}

/**
 * 담당 이름표 — 이 구획·이 이력이 누구 일인지.
 *
 * 상세 화면 어디서든 같은 색·같은 아이콘으로 나오게 이것 하나만 쓴다(`lib/case-owner.ts`).
 * 아이콘을 늘 붙이는 이유는 상태 배지와 같다 — 색만으로는 색약인 사람에게 두 담당이 같아 보인다.
 */
export function OwnerTag({ owner, size = "sm" }: { owner: CaseOwner; size?: "sm" | "md" }) {
  const t = useT()
  return (
    <Badge tone={`owner-${owner}`} size={size} icon={OWNER_ICON[owner]}>
      {t(`owner.${owner}`)}
    </Badge>
  )
}
